import { useState } from 'react';
import {
  Grid2,
  Button,
  Stack,
  Typography,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  IconButton,
  InputAdornment,
  Box
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeSlash } from 'iconsax-react';
import Snackbar from 'utils/Snackbar';
import RequiredIndicator from 'components/@extended/RequiredIndicator';
import { registerTrainingPartner, updateTrainingPartner } from 'services/auth.service';
import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { FormattedMessage } from 'react-intl';
import useAuth from 'hooks/useAuth';
import { useLocation, useNavigate } from 'react-router';

interface AddressValue {
  houseNo: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
}

interface TrainingPartnerFormValues {
  organizationName: string;
  domainUrl: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  panCardId: string;
  address: AddressValue;
}

const AddTrainingCenter = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { organization, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.state?.isEdit || false;
  const editData = location.state?.data || null;
  const orgFetchError = !organization?._id ? 'Organization not found in session' : null;

  const normalizeAddress = (raw: any): AddressValue => {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return {
        houseNo: raw.houseNo ?? '',
        street: raw.street ?? '',
        city: raw.city ?? '',
        state: raw.state ?? '',
        zipCode: raw.zipCode ?? ''
      };
    }
    return { houseNo: '', street: '', city: '', state: '', zipCode: '' };
  };

  const initialValues: TrainingPartnerFormValues = {
    organizationName: editData?.name || '',
    domainUrl: editData?.domainUrl ? editData.domainUrl.replace(/^https?:\/\//, '').split('.')[0] : '',
    firstName: editData?.firstAdminUser?.firstName || '',
    lastName: editData?.firstAdminUser?.lastName || '',
    email: editData?.firstAdminUser?.email || '',
    password: '',
    panCardId: editData?.panCardId || '',
    address: normalizeAddress(editData?.address)
  };

  const validationSchema = Yup.object({
    organizationName: Yup.string().trim().required('Organization name is required'),
    domainUrl: Yup.string()
      .min(5, 'Domain URL must be at least 5 characters long.')
      .max(80, 'Domain URL cannot exceed 80 characters.')
      .matches(
        /^[a-z0-9]+([.-][a-z0-9]+)*$/,
        'Domain URL must be lowercase and contain only letters, numbers, dots, and hyphens, with no spaces or trailing dots/hyphens.'
      )
      .required('Domain name is required.'),
    firstName: Yup.string().trim().required('First name is required'),
    lastName: Yup.string().trim().required('Last name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    password: isEdit
      ? Yup.string().min(6, 'Password must be at least 6 characters').optional()
      : Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
    panCardId: Yup.string()
      .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN format (e.g. ABCDE1234F)')
      .required('PAN is required'),
    address: Yup.object()
      .shape({
        houseNo: Yup.string().max(100).required('House No. is required').trim(),
        street: Yup.string().max(200).required('Street is required').trim(),
        city: Yup.string().max(100).required('City is required').trim(),
        state: Yup.string().max(100).required('State is required').trim(),
        zipCode: Yup.string()
          .max(20)
          .required('Zip Code is required')
          .matches(/^\d+$/, 'Zip Code must contain only numbers')
          .trim()
      })
      .required('Address is required')
  });

  const handleSubmit = async (values: TrainingPartnerFormValues, helpers: FormikHelpers<TrainingPartnerFormValues>) => {
    if (!organization?._id || loading) return;

    setLoading(true);
    helpers.setSubmitting(true);

    const fullDomainUrl = `${window.location.protocol}//${values.domainUrl.trim()}${
      process.env.REACT_APP_HOSTNAME ? `.${process.env.REACT_APP_HOSTNAME}` : '.sapphiresolutions.in.net'
    }`;

    const payload = {
      organizationId: editData?._id,
      userId: editData?.firstAdminUser?._id,
      organizationName: values.organizationName.trim(),
      domainUrl: fullDomainUrl,
      email: values.email.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      panCardId: values.panCardId.trim().toUpperCase(),
      address: {
        houseNo: values.address.houseNo?.trim() ?? '',
        street: values.address.street?.trim() ?? '',
        city: values.address.city?.trim() ?? '',
        state: values.address.state?.trim() ?? '',
        zipCode: values.address.zipCode?.trim() ?? ''
      }
    };

    try {
      let response;

      if (isEdit) {
        const editPayload = values.password?.trim() ? { ...payload, password: values.password.trim() } : payload;
        response = await updateTrainingPartner(editPayload);
      } else {
        response = await registerTrainingPartner({
          ...payload,
          password: values.password,
          isActive: true,
          deleted: true,
          parentOrganizationId: organization?.parentOrganizationId || organization._id,
          packageId: organization?.packageId,
          organizationType: organization?.type,
          storageType: organization?.storageType,
          modulePermissions: organization?.modulePermissions,
          trainingPartnerId: user?._id
        });
      }

      if (response.status === 200 || response.status === 201) {
        Snackbar(isEdit ? 'Training Center updated successfully!' : 'Training Center added successfully!', 'success');
        navigate(-1);
      } else {
        Snackbar(response?.data?.message || 'Something went wrong', 'error');
      }
    } catch (error: any) {
      Snackbar(error?.response?.data?.message || 'Operation failed', 'error');
    } finally {
      setLoading(false);
      helpers.setSubmitting(false);
    }
  };

  return (
    <Grid2 container justifyContent="center">
      <Grid2 size={12}>
        <MainCard
          content={false}
          title={
            <Stack direction="row" spacing={2} alignItems="center">
              <BackToggle show={true} />
              <Stack>
                <Typography variant="h5">
                  <FormattedMessage id={isEdit ? 'Edit Training Center' : 'Add Training Center'} />
                </Typography>
                <Typography variant="body2">
                  <FormattedMessage id={isEdit ? 'Edit Training Center of your organization' : 'Add training center to your organization'} />
                </Typography>
              </Stack>
            </Stack>
          }
        >
          {orgFetchError && (
            <Typography color="error" mb={2} px={2}>
              {orgFetchError}
            </Typography>
          )}

          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isValid, dirty, isSubmitting }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Box p={2}>
                  <Grid2 container spacing={2.5}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <InputLabel>Organization Name <RequiredIndicator /></InputLabel>
                      <OutlinedInput
                        fullWidth
                        name="organizationName"
                        value={values.organizationName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.organizationName && Boolean(errors.organizationName)}
                      />
                      <FormHelperText error>{touched.organizationName && errors.organizationName}</FormHelperText>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <InputLabel>Website / Domain URL <RequiredIndicator /></InputLabel>
                      <OutlinedInput
                        fullWidth
                        name="domainUrl"
                        value={values.domainUrl}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Domain"
                        error={touched.domainUrl && Boolean(errors.domainUrl)}
                        endAdornment={
                          <InputAdornment
                            position="end"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              '&::before': {
                                content: '""',
                                display: 'block',
                                height: '48px',
                                width: '1px',
                                backgroundColor: '#BEC8D0',
                                marginRight: '8px'
                              }
                            }}
                          >
                            <Typography sx={{ fontWeight: 600, color: 'black' }}>
                              {process.env.REACT_APP_HOSTNAME ? `.${process.env.REACT_APP_HOSTNAME}` : '.sapphiresolutions.in.net'}
                            </Typography>
                          </InputAdornment>
                        }
                        sx={{ '& .MuiOutlinedInput-input': { paddingRight: 0 } }}
                      />
                      <FormHelperText error>{touched.domainUrl && errors.domainUrl}</FormHelperText>
                    </Grid2>

                    {[
                      { name: 'firstName', label: 'First Name' },
                      { name: 'lastName', label: 'Last Name' },
                      { name: 'email', label: 'Email Address' },
                      { name: 'panCardId', label: 'PAN Card Number' }
                    ].map((field) => (
                      <Grid2 size={{ xs: 12, md: 6 }} key={field.name}>
                        <InputLabel>{field.label} <RequiredIndicator /></InputLabel>
                        <OutlinedInput
                          fullWidth
                          name={field.name}
                          value={values[field.name as keyof TrainingPartnerFormValues] || ''}
                          onChange={field.name === 'panCardId' ? (e) => { e.target.value = e.target.value.toUpperCase(); handleChange(e); } : handleChange}
                          onBlur={handleBlur}
                          placeholder={field.name === 'panCardId' ? 'ABCDE1234F' : undefined}
                          error={touched[field.name as keyof TrainingPartnerFormValues] && Boolean(errors[field.name as keyof TrainingPartnerFormValues])}
                        />
                        <FormHelperText error>
                          {touched[field.name as keyof TrainingPartnerFormValues]
                            ? (errors[field.name as keyof TrainingPartnerFormValues] as string | undefined) ?? null
                            : null}
                        </FormHelperText>
                      </Grid2>
                    ))}

                    <Grid2 size={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Address <RequiredIndicator /></Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <InputLabel>House No. <RequiredIndicator /></InputLabel>
                      <OutlinedInput
                        fullWidth
                        name="address.houseNo"
                        value={values.address?.houseNo ?? ''}
                        onChange={(e) => setFieldValue('address', { ...values.address, houseNo: e.target.value })}
                        onBlur={handleBlur}
                        placeholder="e.g. 123 or B-12"
                        error={Boolean(touched.address?.houseNo && errors.address && (errors.address as any).houseNo)}
                      />
                      <FormHelperText error>{touched.address?.houseNo && errors.address && (errors.address as any).houseNo}</FormHelperText>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <InputLabel>Street <RequiredIndicator /></InputLabel>
                      <OutlinedInput
                        fullWidth
                        name="address.street"
                        value={values.address?.street ?? ''}
                        onChange={(e) => setFieldValue('address', { ...values.address, street: e.target.value })}
                        onBlur={handleBlur}
                        placeholder="e.g. Main Road, Sector 5"
                        error={Boolean(touched.address?.street && errors.address && (errors.address as any).street)}
                      />
                      <FormHelperText error>{touched.address?.street && errors.address && (errors.address as any).street}</FormHelperText>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <InputLabel>City <RequiredIndicator /></InputLabel>
                      <OutlinedInput
                        fullWidth
                        name="address.city"
                        value={values.address?.city ?? ''}
                        onChange={(e) => setFieldValue('address', { ...values.address, city: e.target.value })}
                        onBlur={handleBlur}
                        placeholder="e.g. Mumbai"
                        error={Boolean(touched.address?.city && errors.address && (errors.address as any).city)}
                      />
                      <FormHelperText error>{touched.address?.city && errors.address && (errors.address as any).city}</FormHelperText>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <InputLabel>State <RequiredIndicator /></InputLabel>
                      <OutlinedInput
                        fullWidth
                        name="address.state"
                        value={values.address?.state ?? ''}
                        onChange={(e) => setFieldValue('address', { ...values.address, state: e.target.value })}
                        onBlur={handleBlur}
                        placeholder="e.g. Maharashtra"
                        error={Boolean(touched.address?.state && errors.address && (errors.address as any).state)}
                      />
                      <FormHelperText error>{touched.address?.state && errors.address && (errors.address as any).state}</FormHelperText>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <InputLabel>Zip Code <RequiredIndicator /></InputLabel>
                      <OutlinedInput
                        fullWidth
                        name="address.zipCode"
                        value={values.address?.zipCode ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 20);
                          setFieldValue('address', { ...values.address, zipCode: val });
                        }}
                        onBlur={handleBlur}
                        placeholder="e.g. 400001"
                        error={Boolean(touched.address?.zipCode && errors.address && (errors.address as any).zipCode)}
                      />
                      <FormHelperText error>{touched.address?.zipCode && errors.address && (errors.address as any).zipCode}</FormHelperText>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <InputLabel>
                        Password {!isEdit && <RequiredIndicator />}
                      </InputLabel>
                      <OutlinedInput
                        fullWidth
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={isEdit ? 'Leave blank to keep current' : undefined}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                              {showPassword ? <Eye /> : <EyeSlash />}
                            </IconButton>
                          </InputAdornment>
                        }
                        error={touched.password && Boolean(errors.password)}
                      />
                      <FormHelperText error>{touched.password && errors.password}</FormHelperText>
                    </Grid2>
                  </Grid2>

                  <Stack direction="row" justifyContent="flex-end" mt={4}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={!dirty || !isValid || isSubmitting || loading || !!orgFetchError}
                    >
                      {loading ? (isEdit ? 'Updating...' : 'Adding...') : isEdit ? 'Update Training Center' : 'Add Training Center'}
                    </Button>
                  </Stack>
                </Box>
              </form>
            )}
          </Formik>
        </MainCard>
      </Grid2>
    </Grid2>
  );
};

export default AddTrainingCenter;
